import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Lightbulb, TrendingUp, CheckCircle2, ArrowRight, Layers, Zap } from 'lucide-react';
export function CaseStudy() {
  return <section id="case-study" className="py-20 bg-[#FDFBF7] relative overflow-hidden" data-id="element-43">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-44">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16 text-center md:text-left" data-id="element-45">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-corporate-teal/10 text-corporate-teal text-xs font-bold uppercase tracking-wider mb-4 border border-corporate-teal/20" data-id="element-46">
            <Zap size={14} data-id="element-47" />
            Flagship Case Study
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-corporate-blue mb-4 leading-tight" data-id="element-48">
            Enterprise Project Controls Transformation
          </h2>
          <p className="text-xl text-[#6B6356] font-medium" data-id="element-49">Pratt & Whitney</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-id="element-50">
          {/* Left Column: Challenge & Strategy */}
          <div className="lg:col-span-7 space-y-8" data-id="element-51">
            {/* Challenge Card */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 rounded-xl shadow-sm border border-[#E8E0D0] relative overflow-hidden group" data-id="element-52">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-400/80" data-id="element-53" />
              <div className="flex items-start gap-4 mb-6" data-id="element-54">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-55">
                  <AlertTriangle className="text-red-500" size={20} data-id="element-56" />
                </div>
                <div data-id="element-57">
                  <h3 className="text-xl font-bold text-corporate-blue" data-id="element-58">
                    Client Challenge
                  </h3>
                  <p className="text-sm text-[#7A7062] mt-1" data-id="element-59">
                    Fragmented systems & disconnected controls
                  </p>
                </div>
              </div>

              <p className="text-[#6B6356] mb-6 leading-relaxed" data-id="element-60">
                Pratt & Whitney was operating with disconnected scheduling
                systems across engineering, finance, and program management. The
                lack of integration between Primavera scheduling and SAP
                financial controls created:
              </p>

              <ul className="space-y-3" data-id="element-61">
                {['Limited executive visibility into project performance', 'Delayed and inconsistent reporting', 'Poor cross-functional coordination', 'Increased risk of cost overruns and schedule slippage'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-[#4A4438]" data-id="element-62">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" data-id="element-63" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            {/* Strategy Card */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 rounded-xl shadow-sm border border-[#E8E0D0] relative overflow-hidden" data-id="element-64">
              <div className="absolute top-0 left-0 w-1 h-full bg-corporate-teal" data-id="element-65" />
              <div className="flex items-start gap-4 mb-6" data-id="element-66">
                <div className="w-10 h-10 rounded-lg bg-corporate-teal/10 flex items-center justify-center flex-shrink-0" data-id="element-67">
                  <Lightbulb className="text-corporate-teal" size={20} data-id="element-68" />
                </div>
                <div data-id="element-69">
                  <h3 className="text-xl font-bold text-corporate-blue" data-id="element-70">
                    Strategy Implemented
                  </h3>
                  <p className="text-sm text-[#7A7062] mt-1" data-id="element-71">
                    Integrated Scheduling & Project Controls Framework
                  </p>
                </div>
              </div>

              <ul className="space-y-4" data-id="element-72">
                {['Engineered and implemented a fully integrated Primavera–SAP scheduling and budget control system', 'Standardized enterprise scheduling structures, reporting models, and performance metrics', 'Built cross-functional workflow alignment across engineering, finance, and program leadership', 'Designed and delivered organization-wide training and adoption framework', 'Established real-time reporting and project performance tracking for leadership visibility'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-[#4A4438]" data-id="element-73">
                    <CheckCircle2 className="w-5 h-5 text-corporate-teal flex-shrink-0 mt-0.5" data-id="element-74" />
                    <span className="leading-relaxed" data-id="element-75">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>

          {/* Right Column: Results & Transformation */}
          <div className="lg:col-span-5 space-y-8" data-id="element-76">
            {/* Results Card */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-corporate-blue text-white p-8 rounded-xl shadow-lg relative overflow-hidden" data-id="element-77">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-corporate-teal/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-78" />

              <div className="flex items-center gap-3 mb-8 relative z-10" data-id="element-79">
                <TrendingUp className="text-corporate-teal" size={24} data-id="element-80" />
                <h3 className="text-xl font-bold" data-id="element-81">Results & Impact</h3>
              </div>

              <ul className="space-y-5 relative z-10" data-id="element-82">
                {['Delivered full integration of scheduling and financial controls', 'Significantly improved executive decision-making speed', 'Increased enterprise project visibility and transparency', 'Strengthened cross-functional coordination efficiency', 'Reduced scheduling inconsistencies and improved execution', 'Enabled organization-wide adoption of standardized controls'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-83">
                    <div className="w-1.5 h-1.5 rounded-full bg-corporate-teal mt-2 flex-shrink-0" data-id="element-84" />
                    <span className="text-gray-200 text-sm leading-relaxed" data-id="element-85">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </motion.div>

            {/* Transformation Summary */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-gradient-to-br from-[#FDFBF7] to-[#F5F0E8] p-8 rounded-xl border border-corporate-teal/30 relative" data-id="element-86">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-corporate-teal via-[#D4B86A] to-corporate-teal" data-id="element-87" />

              <div className="flex items-center gap-2 mb-4" data-id="element-88">
                <Layers className="text-corporate-teal" size={20} data-id="element-89" />
                <h3 className="text-lg font-bold text-corporate-blue" data-id="element-90">
                  Transformation Achieved
                </h3>
              </div>

              <div className="flex flex-col gap-4" data-id="element-91">
                <div className="p-3 bg-white/50 rounded border border-gray-200 text-sm text-gray-500" data-id="element-92">
                  Fragmented, silo-based project planning
                </div>
                <div className="flex justify-center" data-id="element-93">
                  <ArrowRight className="text-corporate-teal rotate-90 md:rotate-0" data-id="element-94" />
                </div>
                <div className="p-4 bg-white rounded border-l-4 border-corporate-teal shadow-sm text-sm font-medium text-corporate-blue leading-relaxed" data-id="element-95">
                  Fully integrated, data-driven enterprise project control
                  environment capable of supporting large-scale, high-value
                  programs with precision.
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}